import EventEmitter from 'events';
import createLogger from '@exodus/logger';
import assert from 'minimalistic-assert';
import { LifecycleHook as Hook } from './constants.js';
// Because we are forced to restart wallet after deleting/importing, we need to store certain flags to persist state between executions
// Triggers deletetion logic when wallet starts.
// Set as true on delete method is called, and set to false when wallet is restarted
const DELETE_FLAG = 'deleteFlag';
// Triggers import logic when wallet starts. Used when overwriting existing wallet (not importing one from scratch)
// Set as true on import method is called, and set to false when wallet is restarted
const IMPORT_FLAG = 'importFlag';
// Triggers restore logic. We need to do it after any import, no matter if there was a previous wallet or not
// Set as true on import method is called, and set to false after restore is completed
const RESTORE_FLAG = 'restoreFlag';
// Stores latest connected seed credential id. Persists after lock.
// Used to clear storage if another wallet was connected on the passkey side.
const LATEST_SEED_ID_FLAG = 'latestSeedId';
const HOOKS = new Set(Object.values(Hook));
export class Application extends EventEmitter {
    #hooks = {};
    #wallet = null;
    #storage = null;
    #lockedAtom = null;
    #credentialIdAtom;
    #applicationStarted;
    #resolveStart = undefined;
    #logger;
    constructor({ wallet, unsafeStorage, lockedAtom, credentialIdAtom, logger }) {
        super();
        super.setMaxListeners(Number.POSITIVE_INFINITY);
        this.#wallet = wallet;
        this.#lockedAtom = lockedAtom;
        this.#credentialIdAtom = credentialIdAtom;
        this.#storage = unsafeStorage.namespace('flags');
        this.#logger = logger ?? createLogger('Application');
        this.#applicationStarted = new Promise((resolve) => (this.#resolveStart = resolve));
        // Auto-unlock wallet with previously connected credential id
        this.#unlockIfConnected();
    }
    #unlockIfConnected = async () => {
        const storedCredentialId = await this.#credentialIdAtom.get();
        if (!storedCredentialId) {
            return;
        }
        this.unlock();
    };
    start = async () => {
        const [deleteFlag, importFlag] = await this.#storage.batchGet([
            DELETE_FLAG,
            IMPORT_FLAG,
        ]);
        const isDeleting = !!deleteFlag;
        const isImporting = !!importFlag;
        if (isDeleting) {
            await this.#wallet.clear();
        }
        if (isDeleting || isImporting) {
            await this.#storage.batchDelete([DELETE_FLAG, IMPORT_FLAG]);
        }
        const walletExists = await this.#wallet.exists();
        if (isImporting) {
            await this.fire(Hook.Clear, null, { concurrent: true });
        }
        if (isImporting) {
            await this.fire(Hook.Import);
        }
        const [isLocked, isRestoring] = await Promise.all([
            this.#wallet.isLocked(),
            this.isRestoring(),
        ]);
        await this.fire(Hook.Start, {
            walletExists,
            isLocked,
            isRestoring,
        });
        const locked = await this.#wallet.isLocked();
        this.#logger.log('wallet is locked', locked);
        this.#resolveStart?.();
    };
    load = async () => {
        await this.#applicationStarted;
        const [walletExists, isLocked, isRestoring] = await Promise.all([
            this.#wallet.exists(),
            this.#wallet.isLocked(),
            this.isRestoring(),
        ]);
        await this.fire(Hook.Load, {
            walletExists,
            isLocked,
            isRestoring,
        });
    };
    unload = async () => {
        await this.#applicationStarted;
        await this.fire(Hook.Unload);
    };
    hook = (hookName, listener) => {
        assert(HOOKS.has(hookName), `no such hook: ${hookName}`);
        if (!this.#hooks[hookName]) {
            this.#hooks[hookName] = [];
        }
        this.#hooks[hookName].push(listener);
    };
    fire = async (hookName, params, { concurrent } = {}) => {
        assert(HOOKS.has(hookName), `no such hook: ${hookName}`);
        this.#logger.debug(`firing hooks ${concurrent ? 'concurrently' : 'sequentially'}`, hookName);
        const hooks = this.#hooks[hookName] || [];
        const hookFns = hooks.map((hook) => async () => {
            try {
                await hook(params);
            }
            catch (error) {
                this.#logger.error(`application lifecycle hook failed: ${hookName}`, hook.name, params, error);
                throw error;
            }
        });
        if (concurrent) {
            await Promise.allSettled(hookFns.map((promise) => promise()));
        }
        else {
            for (let index = 0; index < hookFns.length; index++) {
                await hookFns[index]();
            }
        }
        super.emit(hookName, params);
    };
    create = async (opts) => {
        this.#logger.log('creating wallet');
        await this.#applicationStarted;
        await this.#wallet.create(opts);
        const isLocked = await this.#wallet.isLocked();
        await this.fire(Hook.Create, {
            isLocked,
            isRestoring: false,
            walletExists: true,
        });
    };
    import = async (opts) => {
        this.#logger.log('importing wallet');
        await this.#storage.set(RESTORE_FLAG, true);
        await this.#applicationStarted;
        const walletExists = await this.#wallet.exists();
        const { forceRestart, ...wallet } = opts;
        await this.#wallet.import(wallet);
        await this.fire(Hook.Backup);
        if (forceRestart || walletExists) {
            await this.#storage.set(IMPORT_FLAG, true);
            await this.fire(Hook.Restart, { reason: 'import' });
        }
        else {
            await this.fire(Hook.Import);
            this.#logger.log('wallet imported');
        }
    };
    getMnemonic = async (opts) => this.#wallet.getMnemonic(opts);
    setBackedUp = async () => {
        await this.fire(Hook.Backup);
    };
    lock = async () => {
        this.#logger.log('locking');
        await this.#applicationStarted;
        await this.#wallet.lock();
        await this.#lockedAtom.set(true);
        await this.#credentialIdAtom.set(undefined);
        await this.fire(Hook.Lock);
        this.#logger.log('locked');
        // TODO: Make it into an adapter?
        window.location?.reload?.();
    };
    #restoreIfNeeded = async () => {
        const isRestoring = await this.isRestoring();
        if (isRestoring) {
            await this.fire(Hook.Restore);
            await this.#storage.delete(RESTORE_FLAG);
            await this.fire(Hook.RestoreCompleted);
        }
        this.fire(Hook.AssetsSynced).catch((error) => {
            console.warn('failed to execute onAssetsSynced hooks', error);
        });
    };
    restore = async () => {
        await this.#storage.set(RESTORE_FLAG, true);
        await this.#restoreIfNeeded();
    };
    unlock = async (...args) => {
        this.#logger.log('unlocking');
        const currentCredentialId = await this.#credentialIdAtom.get();
        const credentialId = currentCredentialId || (await this.#wallet.unlock(...args));
        if (!credentialId) {
            throw new Error(`Couldn't connect to wallet`);
        }
        // Clear if other wallet was used
        const latestCredentialId = await this.#storage.get(LATEST_SEED_ID_FLAG);
        if (latestCredentialId && latestCredentialId !== credentialId) {
            this.#logger.debug('logged in with different credential, clearing storage');
            await this.fire(Hook.Clear, null, { concurrent: true });
        }
        // Load features in background.
        this.load();
        await this.#credentialIdAtom.set(credentialId);
        await this.#storage.set(LATEST_SEED_ID_FLAG, credentialId);
        await this.#lockedAtom.set(false);
        // Make sure application is started before entering other lifecycles.
        await this.#applicationStarted;
        await this.fire(Hook.Migrate);
        await this.fire(Hook.Unlock);
        this.#restoreIfNeeded();
        this.#logger.log('unlocked');
    };
    delete = async () => {
        await this.#storage.set(DELETE_FLAG, true);
        await this.fire(Hook.Restart, { reason: 'delete' });
    };
    stop = async () => {
        await this.fire(Hook.Stop);
    };
    isRestoring = async () => {
        return this.#storage.get(RESTORE_FLAG);
    };
}
const createApplication = (args = {}) => new Application({ ...args });
const applicationDefinition = {
    id: 'application',
    type: 'module',
    factory: createApplication,
    public: true,
    dependencies: [
        'unsafeStorage',
        'lockedAtom',
        'credentialIdAtom',
        'wallet',
        'logger',
    ],
};
export default applicationDefinition;
